import React, { useMemo } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Building2, MapPin, ReceiptText, ShieldCheck } from "lucide-react";

function B2BCheckoutPage() {
  const navigate = useNavigate();
  const brand = useMemo(
    () => ({
      accent: "#0EA5E9",
      accent2: "#22C55E",
      bg: "#F7FAFC",
      text: "#0F172A",
      subtext: "rgba(15, 23, 42, 0.72)",
      stroke: "rgba(2, 6, 23, 0.10)",
    }),
    []
  );

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: brand.bg, py: 6 }}>
      <Container maxWidth="lg">
        <Box
          sx={{
            borderRadius: 4,
            overflow: "hidden",
            border: `1px solid ${brand.stroke}`,
            backgroundImage:
              'linear-gradient(90deg, rgba(14,165,233,0.12), rgba(34,197,94,0.10)), url("https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1400&h=320&fit=crop")',
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <Box sx={{ p: { xs: 3, md: 4 } }}>
            <Typography variant="h4" sx={{ fontWeight: 950, color: brand.text }}>
              B2B Checkout
            </Typography>
            <Typography sx={{ color: brand.subtext, mt: 0.5 }}>
              Add billing + shipping details and place the order.
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: "wrap" }}>
              <Chip icon={<ShieldCheck size={16} />} label="Invoice-ready" />
              <Chip icon={<ReceiptText size={16} />} label="GST format" />
              <Chip icon={<MapPin size={16} />} label="Ship-to address" />
            </Stack>
          </Box>
        </Box>

        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} md={7}>
            <Card sx={{ borderRadius: 4, border: `1px solid ${brand.stroke}` }}>
              <CardContent>
                <Typography sx={{ fontWeight: 950, color: brand.text, mb: 2 }}>
                  Company & delivery details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Company name" defaultValue="MAP Edu Services" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="GSTIN" defaultValue="29ABCDE1234F1Z5" />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Ship-to address" defaultValue="Hyderabad, Telangana, India" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="PO number (optional)" placeholder="PO-2026-0041" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Payment terms" defaultValue="Net-30" />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate("/B2BCart")}
                    sx={{ borderRadius: 2.5, textTransform: "none", fontWeight: 950 }}
                  >
                    Back to cart
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => navigate("/B2BOrders")}
                    sx={{
                      borderRadius: 2.5,
                      textTransform: "none",
                      fontWeight: 950,
                      color: "white",
                      background: `linear-gradient(135deg, ${brand.accent} 0%, ${brand.accent2} 100%)`,
                    }}
                  >
                    Place order
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={5}>
            <Card sx={{ borderRadius: 4, border: `1px solid ${brand.stroke}` }}>
              <CardContent>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: 2, display: "grid", placeItems: "center", bgcolor: "rgba(14,165,233,0.10)" }}>
                    <Building2 size={18} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 950, color: brand.text }}>Invoice preview</Typography>
                    <Typography variant="body2" sx={{ color: brand.subtext }}>
                      Summary shown before confirmation.
                    </Typography>
                  </Box>
                </Stack>

                <Divider sx={{ my: 1.5 }} />

                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" sx={{ color: brand.subtext }}>
                      Subtotal
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 950, color: brand.text }}>
                      ₹(auto)
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" sx={{ color: brand.subtext }}>
                      Shipping
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 950, color: brand.text }}>
                      ₹(auto)
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" sx={{ color: brand.subtext }}>
                      Taxes (GST)
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 950, color: brand.text }}>
                      ₹(auto)
                    </Typography>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default B2BCheckoutPage;

